/**
 * L0 CX Score 体验打分面板（W5）· 对话级 LLM judge 接后台。
 * 选/输入一段完整客服对话 → 运行 judge → cx_score[1,5] + 7 reasons + 封顶 + 解释。
 */
import { TriangleAlert } from "lucide-react";
import { useMemo, useState } from "react";

import { AiCard } from "@/components/ui/ai-card";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
	type CxScoreConversationTurn,
	type CxScoreRunResponse,
	useRunCxScore,
} from "@/lib/api/eval";

const SAMPLE = `用户: 我上周买的鞋子还没收到，订单号 1023，能查下吗？
客服: 帮您查到订单 1023 已发货，物流单号 SF1234567，预计明天送达。
用户: 好的，那如果明天没到怎么办？
客服: 若明天 20:00 前未收到，您可以直接申请补发或退款，我已为您备注。`;

// 7 reasons 的中文名 + High 方向（true=High好 / false=High差 / null=Pos/Neg 维度）。
const REASON_META: Record<string, { label: string; highIsGood: boolean | null }> = {
	resolution_outcome: { label: "问题解决", highIsGood: true },
	answer_clarity_completeness: { label: "回答清晰完整", highIsGood: true },
	customer_effort: { label: "用户费力度", highIsGood: false },
	strong_emotion: { label: "强烈情绪", highIsGood: null },
	product_service_feedback: { label: "商品/服务反馈", highIsGood: null },
	policy_feedback: { label: "政策反馈", highIsGood: null },
	compliance_risk: { label: "合规风险", highIsGood: false },
};

function parseConversation(text: string): CxScoreConversationTurn[] {
	const turns: CxScoreConversationTurn[] = [];
	for (const raw of text.split("\n")) {
		const line = raw.trim();
		if (!line) continue;
		const m = line.match(/^(用户|客服|user|agent)\s*[:：]\s*(.+)$/i);
		if (!m) continue;
		const tag = (m[1] ?? "").toLowerCase();
		const content = (m[2] ?? "").trim();
		if (!content) continue;
		const role = tag === "用户" || tag === "user" ? "user" : "agent";
		turns.push({ role, content });
	}
	return turns;
}

function reasonTone(value: string, highIsGood: boolean | null): string {
	const base =
		"inline-flex w-fit rounded-full border border-surface-border px-2 py-0.5 text-xs font-medium";
	if (value === "Positive") return `${base} bg-surface-card text-brand-success`;
	if (value === "Negative") return `${base} bg-surface-card text-brand-danger`;
	if (value === "High")
		return `${base} bg-surface-card ${highIsGood ? "text-brand-success" : "text-brand-danger"}`;
	if (value === "Low")
		return `${base} bg-surface-card ${highIsGood === false ? "text-brand-success" : "text-brand-warning"}`;
	return `${base} bg-surface-subtle text-text-muted`; // Neutral
}

function scoreTone(score: number): string {
	if (score >= 4) return "text-brand-success";
	if (score === 3) return "text-brand-warning";
	return "text-brand-danger";
}

export function CxScorePanel() {
	const [text, setText] = useState(SAMPLE);
	const run = useRunCxScore();
	const [result, setResult] = useState<CxScoreRunResponse | null>(null);
	const turns = useMemo(() => parseConversation(text), [text]);

	function handleRun() {
		setResult(null);
		run.mutate(
			{ conversation: turns, source: "admin-panel" },
			{ onSuccess: (data) => setResult(data) },
		);
	}

	return (
		<Card data-testid="m07-cx-score-panel">
			<CardHeader>
				<CardTitle className="text-lg">L0 体验打分（CX Score）</CardTitle>
				<CardDescription>
					对话级 LLM judge：给一段完整客服对话打 1–5 分 + 7 条 reasons + 中文解释。 Compliance=High
					硬封顶 ≤2。规则：至少 1 轮用户 + 1 轮客服。
				</CardDescription>
			</CardHeader>
			<CardContent className="space-y-3">
				<label className="grid gap-1 text-sm">
					<span className="text-xs text-text-muted">
						对话（每行 <code>用户:</code> 或 <code>客服:</code> 开头）
					</span>
					<textarea
						value={text}
						onChange={(event) => setText(event.target.value)}
						rows={6}
						className="rounded-md border border-surface-border bg-surface-background p-3 font-mono text-sm leading-6"
						data-testid="cx-score-input"
					/>
				</label>
				<div className="flex items-center gap-3">
					<Button
						type="button"
						onClick={handleRun}
						disabled={run.isPending || turns.length === 0}
						data-testid="cx-score-run"
					>
						{run.isPending ? "打分中…" : "运行打分"}
					</Button>
					<span className="text-xs text-text-muted">解析到 {turns.length} 轮对话</span>
				</div>

				{run.isError ? (
					<div className="text-sm text-brand-danger" data-testid="cx-score-error">
						调用失败，请重试。
					</div>
				) : null}

				{result ? (
					<div data-testid="cx-score-result">
						{result.scored ? (
							<AiCard title={`CX rating: ${result.cx_score} / 5`}>
								<div className="space-y-3">
									<div className="flex items-baseline gap-3">
										<span
											className={`text-3xl font-semibold ${scoreTone(result.cx_score ?? 0)}`}
											data-testid="cx-score-value"
										>
											{result.cx_score}
										</span>
										{result.compliance_capped ? (
											<span className="inline-flex items-center gap-1 rounded-full border border-surface-border bg-surface-card px-2 py-0.5 text-xs font-medium text-brand-danger">
												<TriangleAlert className="h-3.5 w-3.5" aria-hidden="true" />
												Compliance=High 已封顶 ≤2
											</span>
										) : null}
									</div>
									<div className="grid gap-2 sm:grid-cols-2">
										{Object.entries(REASON_META).map(([key, meta]) => {
											const value = result.reasons?.[key] ?? "Neutral";
											return (
												<div key={key} className="flex items-center justify-between gap-2">
													<span className="text-sm text-text-secondary">{meta.label}</span>
													<span className={reasonTone(value, meta.highIsGood)}>{value}</span>
												</div>
											);
										})}
									</div>
									<div className="text-sm leading-6 text-text-secondary">{result.explanation}</div>
									<div className="text-xs text-text-muted">模型：{result.model}</div>
								</div>
							</AiCard>
						) : (
							<div
								className="flex gap-2 text-sm text-text-secondary"
								data-testid="cx-score-unscored"
							>
								<TriangleAlert
									className="mt-0.5 h-4 w-4 flex-none text-brand-warning"
									aria-hidden="true"
								/>
								<span>未评分：{result.unscored_reason}</span>
							</div>
						)}
					</div>
				) : null}
			</CardContent>
		</Card>
	);
}
